import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const paymentId = params.get("id");
  const statusQuery = params.get("status");
  const [state, setState] = useState("loading"); // loading | ok | fail
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!paymentId) {
      setState("fail");
      setMsg("Missing payment id");
      return;
    }
    api
      .get(`/payments/moyasar/verify?payment_id=${encodeURIComponent(paymentId)}`)
      .then(({ data }) => {
        if (data.ok) setState("ok");
        else { setState("fail"); setMsg(data.status || statusQuery || ""); }
      })
      .catch((e) => { setState("fail"); setMsg(formatApiError(e)); });
  }, [paymentId, statusQuery]);

  return (
    <main className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 sm:px-6 py-8 sm:py-12" data-testid="payment-callback-page">
      <Card className="w-full max-w-md p-6 sm:p-8 border border-slate-200 shadow-sm text-center">
        {state === "loading" && (<><Loader2 className="w-10 h-10 text-slate-700 animate-spin mx-auto mb-4" /><p className="text-slate-600">{lang === "ar" ? "نتحقق من الدفع…" : "Verifying payment…"}</p></>)}
        {state === "ok" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" data-testid="payment-ok-icon" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">
              {lang === "ar" ? "تم استلام طلبك بنجاح ✅" : "Your order was received ✅"}
            </h1>
            <div className="space-y-3 mb-6 text-start">
              <p className="text-sm text-slate-700 leading-relaxed">
                {lang === "ar"
                  ? "شكراً لك! تم تأكيد الدفع ووضع بحثك في قائمة الإنتاج."
                  : "Thank you! Your payment is confirmed and your paper is now queued for generation."}
              </p>
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm leading-relaxed">
                <p className="font-semibold mb-1">
                  {lang === "ar" ? "ماذا يحدث الآن؟" : "What happens next?"}
                </p>
                <ul className="list-disc ps-5 space-y-1 text-[13px]">
                  <li>
                    {lang === "ar"
                      ? "يبدأ نظامنا توليد بحثك تلقائياً خلال ٥ إلى ١٥ دقيقة."
                      : "Our system starts generating your paper within 5 to 15 minutes."}
                  </li>
                  <li>
                    {lang === "ar"
                      ? "فور الانتهاء، سيصلك بريدٌ إلكتروني يحتوي ملف Word الجاهز."
                      : "As soon as it's ready, you'll receive an email with the Word file attached."}
                  </li>
                  <li>
                    {lang === "ar"
                      ? "يمكنك أيضاً متابعة الحالة من «طلباتي» في أي وقت."
                      : "You can also track the status from 'My Orders' at any time."}
                  </li>
                </ul>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                {lang === "ar"
                  ? "💡 لا تحتاج إبقاء هذه الصفحة مفتوحة — التوليد يستمر في الخلفية."
                  : "💡 You don't need to keep this page open — generation continues in the background."}
              </p>
            </div>
            <Button
              onClick={() => navigate("/dashboard?tab=archive")}
              className="bg-slate-900 hover:bg-slate-800 w-full h-12 text-base"
              data-testid="payment-ok-go-orders-btn"
            >
              {lang === "ar" ? "متابعة طلبي في «طلباتي»" : "Track my order"}
            </Button>
          </>
        )}
        {state === "fail" && (
          <>
            <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" data-testid="payment-fail-icon" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
              {lang === "ar" ? "تعذر إكمال الدفع" : "Payment not completed"}
            </h1>
            {msg && <p className="text-sm text-slate-600 mb-4">{msg}</p>}
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              {lang === "ar" ? "العودة" : "Back"}
            </Button>
          </>
        )}
      </Card>
    </main>
  );
}
